<template lang="pug">
  .holidays-list-wrapper
    h2 feriados

    .details(v-if="showDetails")
      HolidayDetails(@close="closeDetails" :holiday="editingHoliday")

    template(v-else)
      label total de feriados: {{ totalHolidays }}

      .buttons
        PButton(color="primary" size="md" @click="genReport") gerar relatório
        PButton(color="warning" size="md" @click="createNewHoliday") criar novo feriado

    SmartTable.table(name="holidays-list" v-bind="table", v-model="tableData" ref="table")
      template(v-slot:default="slotProps")
        TableRow(v-bind="slotProps")
          template(v-slot:name="{ columnValue }")
            span.no-wrap(:title="columnValue") {{ columnValue }}

          template(v-slot:date="{ columnValue, rowItem, index }")
            | {{ formatDate(rowItem) }}

          template(v-slot:region="{ columnValue, rowItem, index }")
            span.no-wrap(:title="regionsPrettyName ? regionsPrettyName[columnValue.id] : 'carregando'")
              | {{ regionsPrettyName ? regionsPrettyName[columnValue.id] : 'carregando' }}

          template(v-slot:isRestDay="{ columnValue, rowItem, index }")
            .icon.fs-19.icon-ptt-icons-obrig.color-success(v-if="columnValue === true")
            .icon.fs-19.icon-ptt-icons-nao-obrig.color-danger(v-else)

          template(v-slot:repeats="{ columnValue, rowItem, index }")
            .icon.fs-19.icon-ptt-icons-obrig.color-success(v-if="columnValue === true")
            .icon.fs-19.icon-ptt-icons-nao-obrig.color-danger(v-else)

          template(v-slot:customRules="{ columnValue, rowItem, index }")
            | {{ columnValue.name }}

      template(v-slot:detailsBox="{item}")
        .details
          DBLabel(title="nome:" :body="item.name")
          DBLabel(title="data:" :body="formatDate(item)")
          DBLabel(v-if="regionsPrettyName" title="região:" :body="regionsPrettyName[item.region.id]")
          DBLabel(title="feriado fixo:" :body="item.repeats ? 'sim' : 'não'")
          DBLabel(:title="$ws('customRules')+':'" :body="item.customRules.name")
          DBLabel(title="folga:" :body="item.isRestDay ? 'sim' : 'não'")

          .buttons
            PButton(color="info" size="md" @click="editHoliday(item)", title="editar")
              span.icon-ptt-icons-lapis

            PConfirmButton(text="Excluir este feriado?" color="danger" size="md" @click="deleteHoliday(item.id)" :loading="deleting" title="excluir")
              span.icon-ptt-icons-excluir

</template>

<script>
import moment from 'moment'
import {includesSearch, getHierarchyName} from 'src/utils'
import HolidayDetails from '../details/HolidayDetails'
import { setAmplitudeEvent } from 'src/amplitudeUtils'

export default {
  name: 'HolidaysList',
  components: {HolidayDetails},
  created () {
    this.getHolidays()
    this.getRegions()
  },
  mounted () {
    setAmplitudeEvent('lista feriados', {})
  },
  data () {
    return {
      deleting: false,
      showDetails: false,
      editingHoliday: null,
      reloadingTable: false,
      holidays: [],
      table: {
        headerItems: [
          {column: 'nome', field: 'name', width: '1fr'},
          {
            column: 'região',
            field: 'region',
            width: '0.6fr',
            filterFunction: ({search, item}) => {
              return includesSearch(this.regionsPrettyName[item.region.id], search)
            }
          },
          {
            column: 'data',
            field: 'date',
            width: '0.4fr',
            filterFunction: ({search, item}) => {
              return includesSearch(this.formatDate(item), search)
            }
          },
          {
            column: 'fixo',
            field: 'repeats',
            width: '0.3fr',
            filterFunction: ({search, item}) => {
              if (search === 'todos') return true
              return search === 'sim' ? item.repeats === true : item.repeats === false
            },
            options: [
              {iconClass: '', text: 'todos'},
              {iconClass: 'icon-ptt-icons-obrig', text: 'sim'},
              {iconClass: 'icon-ptt-icons-nao-obrig', text: 'não'}
            ]
          },
          {
            column: 'folga',
            field: 'isRestDay',
            width: '0.3fr',
            filterFunction: ({search, item}) => {
              if (search === 'todos') return true
              return search === 'sim' ? item.isRestDay === true : item.isRestDay === false
            },
            options: [
              {iconClass: '', text: 'todos'},
              {iconClass: 'icon-ptt-icons-obrig', text: 'sim'},
              {iconClass: 'icon-ptt-icons-nao-obrig', text: 'não'}
            ]
          },
          {
            column: this.$ws('customRules'),
            field: 'customRules',
            width: '0.6fr',
            filterFunction: ({search, item}) => {
              return includesSearch(item.name, search)
            }
          }
        ],
        config: {
          perPage: 10,
          startFocusedSearch: 'name',
          defaultSortField: 'name',
          defaultColumnSearch: 'name'
        }
      }
    }
  },
  computed: {
    totalHolidays () {
      return this.holidays.length
    },
    tableData: {
      get () {
        return {
          loaded: this.holidays,
          loading: !this.holidays.length
        }
      }
    },
    regionsPrettyName () {
      const regions = this.$store.state.common.regions.items

      if (!regions.length) return null

      const result = {}

      regions.forEach(region => {
        result[region.id] = getHierarchyName(region, regions)
      })

      return result
    }
  },
  methods: {
    createNewHoliday () {
      this.editingHoliday = null
      this.showDetails = true
    },
    editHoliday (holiday) {
      this.showDetails = true
      this.editingHoliday = holiday
    },
    async deleteHoliday (holidayId) {
      this.deleting = true
      try {
        await this.$API.holiday.delete({id: holidayId})
        this.$swal({
          type: 'success',
          text: 'Feriado deletado com sucesso'
        })
        this.closeDetails(true)
      } catch (e) {
        this.$swal({
          type: 'error',
          text: e.data.error || 'Não foi possível apagar o feriado'
        })
      }
      this.deleting = false
    },
    async getHolidays () {
      const holidays = await this.$API.holiday.get()
      this.holidays = holidays.data.success
    },
    async getRegions () {
      this.$store.dispatch('common/regions/fetchItems')
    },
    formatDate (holiday) {
      return holiday.repeats
        ? moment(holiday.date).format('DD MMM')
        : moment(holiday.date).format('DD MMM YYYY')
    },
    closeDetails (reloadTable) {
      this.showDetails = false
      this.$refs.table.closeDetailsBox()
      if (reloadTable) {
        this.getHolidays()
        this.getRegions()
      }
    },
    async genReport () {
      let result
      try {
        result = await this.$API.holidaysReport.get()
        this.$modalDeSucesso(result.data.success)
      } catch (e) {
        console.error(e)
        this.$modalDeErro(e?.data?.error ? e.data.error : 'Não foi possível gerar o relatório')
      }
    }
  }
}
</script>

<style scoped lang="scss">
.holidays-list-wrapper {
  display: grid;
  grid-template-columns: 0.4fr 1fr;
  align-items: baseline;

  .buttons {
    display: grid;
    grid-auto-flow: column;
    grid-column: 2/3;
    grid-gap: 1px;
    justify-self: right;
    margin-bottom: var(--header-margin-bottom);
  }

  .details {
    grid-column: 1/3;
  }

  .flex {
    display: grid;
    grid-row-gap: 30px;
    grid-template-columns: 1fr 1fr 1fr;

    .name {
      grid-column: 1/4;
    }
    .buttons {
      display: grid;
      grid-gap: 10px;
      grid-template-columns: min-content min-content;
      grid-column: 1;
      grid-auto-flow: column;
    }
  }

  h2 {
    grid-column: 1/3;
    text-align: center;
  }
  .table {
    grid-column: 1/3;

    .loading-field {
      width: 30%;
      height: 60%;
      background-color: lightgray;
    }

    .details {
      height: 100%;
      display: grid;
      grid-row-gap: 20px;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto auto 1fr;

      .buttons {
        grid-column: 1/3;
        grid-row: 4;
        justify-self: right;
        display: grid;
        grid-gap: 2px;
        grid-auto-flow: column;
      }
    }
  }
}
</style>
